import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {API_URL} from "../App";


// Simple easing function (easeInOut)
const easeInOut = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

const BotItem = React.memo(({ bot, index }) => {
  const [rank, setRank] = useState(bot.previousRank);
  const [elo, setElo] = useState(bot.elo);
  const navigate = useNavigate();

  // Smoothly interpolate Elo value with easeInOut easing
  useEffect(() => {
    if (bot.elo !== elo) {
      let startTime;
      const duration = 2100; // Duration of transition (in ms)
      const startElo = elo;
      const targetElo = bot.elo;

      const animateElo = (timestamp) => {
        if (!startTime) startTime = timestamp;

        const progress = (timestamp - startTime) / duration;
        const easedProgress = easeInOut(progress);
        const newElo = (startElo + (targetElo - startElo) * easedProgress);

        setElo(newElo);

        if (progress < 1) {
          requestAnimationFrame(animateElo);
        } else {
          setElo(targetElo); // Ensure the Elo reaches the exact target value
        }
      };

      requestAnimationFrame(animateElo);
    }
  }, [bot.elo]);

  // Smoothly interpolate Rank change with easeInOut easing
  useEffect(() => {
    if (bot.previousRank !== (index+1)) {
      let startTime;
      const duration = 2100; // Duration of transition (in ms)
      const startRank = bot.previousRank;
      const targetRank = index+1;

      const animateRank = (timestamp) => {
        if (!startTime) startTime = timestamp;

        const progress = (timestamp - startTime) / duration;
        const easedProgress = easeInOut(progress);
        const newRank = Math.round(startRank + (targetRank - startRank) * easedProgress);

        setRank(newRank);

        if (progress < 1) {
          requestAnimationFrame(animateRank);
        } else {
          setRank(targetRank); // Ensure the rank reaches the exact target value
        }
      };

      requestAnimationFrame(animateRank);
    }
  }, [index]);

  return (
    <motion.div
      key={bot.tag}
      initial={{ opacity: 0, y: 20 }} // Initial animation when entering
      animate={{ opacity: 1, y: 0 }} // Animate to visible
      exit={{ opacity: 0, y: 60 }} // Exit animation when leaving
      transition={{ duration: 1.2, ease: "easeInOut" }}
      layout // Automatically handle positional changes
      className="max-w-sm mx-auto flex items-center py-0.5 px-4 bg-gray-800/70 rounded-lg mb-3 cursor-pointer hover:bg-gray-700/70"
      onClick={() => navigate(`/profiles/${bot.twitch_display_name}/${bot.tag}`)}
    >
      {/* Rank with animated transition */}
      <motion.div
        className={getRankStyle(rank)}
        key={rank}
        animate={{
          opacity: 1,
          transition: {
            duration: 1.2,
            ease: "easeInOut",
          },
        }}
      >
        {rank}
      </motion.div>

      {/* Character Icon */}
      <div className="w-12 flex justify-start">
        <img
          src={`/icons/${bot.character}_${bot.costume_id}.webp`}
          alt=""
          className="w-8 h-8"
        />
      </div>

      {/* Tag and Twitch Info */}
      <div className="w-40">
        <p className="text-left text-sm truncate" title={bot.tag}>
          {bot.tag}
        </p>
        <p className="text-left text-sm text-gray-400 truncate" title={bot.twitch_display_name}>
          @{bot.twitch_display_name}
        </p>
      </div>

      {/* Elo with smooth update */}
      <motion.div
        className="w-10 text-left"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
      >
        <motion.span
          key={elo}
          animate={{
            opacity: 1,
            transition: {
              duration: 1.2,
              ease: "easeInOut", // Applying easeInOut transition for Elo
            },
          }}
        >
          {elo.toFixed(0)}
        </motion.span>
      </motion.div>
    </motion.div>
  );
});

// Rank styling helper
const getRankStyle = (rank) => {
  switch (rank) {
    case 1:
      return "w-20 text-left text-xl text-yellow-500"; // Gold
    case 2:
      return "w-20 text-left text-xl text-gray-300"; // Silver
    case 3:
      return "w-20 text-left text-xl text-yellow-700"; // Bronze
    default:
      return "w-20 text-left text-sm text-white"; // Default rank color
  }
};

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaderboard`); // Flask backend route
      const newLeaderboard = response.data;
      setLeaderboard((prevLeaderboard) => {
        // Map the previous leaderboard to preserve rank changes
        const prevMap = new Map(prevLeaderboard.map((bot, index) => [bot.tag, index + 1]));
        return newLeaderboard.map((bot, index) => ({
          ...bot,
          previousRank: prevMap.get(bot.tag) || (index+1),
        }));
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  }, []); // Memoized fetchLeaderboard to prevent re-creation on every render

  useEffect(() => {
    fetchLeaderboard(); // Initial fetch
    const interval = setInterval(fetchLeaderboard, 60000); // Update every 60 seconds
    return () => clearInterval(interval); // Clean up interval
  }, [fetchLeaderboard]);

  if (isLoading) {
    return <div className="text-center text-gray-300">Loading leaderboard...</div>;
  }

  return (
    <div className="">
      <h2 className="text-2xl font-bold text-center mb-4 py-3">Top 15</h2>

      {/* Header */}
      <div className="max-w-xs lg:max-w-sm mx-auto flex items-center py-2 px-4 mb-2 bg-gray-900/50 text-gray-400 font-bold rounded-lg">
        <div className="w-20 text-left">Rank</div>
        <div className="w-12 text-left">Main</div>
        <div className="w-40 text-left">Tag</div>
        <div className="w-10 text-left">Elo</div>
      </div>

      {/* Leaderboard */}
      <div className="max-w-xs lg:max-w-sm pb-40 mx-auto overflow-visible">
        <AnimatePresence>
          {leaderboard.map((bot, index) => (
            <BotItem key={bot.tag} bot={bot} index={index} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Leaderboard;
